import type { GetToken } from '@clerk/types'

import apiFactory from '@snowball-tech/common/api'
import commonEndpoints from '@snowball-tech/common/api/endpoints'

import type { Edition, User } from '@/types'

/** ****************************************************************************
 * Editions
 **************************************************************************** */

function latestEditions(api: ReturnType<typeof apiFactory>) {
  return async () => api<Array<Edition>>('/media/editions/latest')
}

/** ****************************************************************************
 * Payments
 **************************************************************************** */

function generateCheckoutLink(api: ReturnType<typeof apiFactory>) {
  return async (
    plan: 'monthly' | 'yearly',
    urls: {
      error: string
      success: string
    },
  ) => {
    const result = await api<
      { redirectUrl: string },
      {
        errorUrl: string
        plan: typeof plan
        successUrl: string
      }
    >(
      '/api/payment',
      {
        errorUrl: urls.error,
        plan,
        successUrl: urls.success,
      },
      {
        method: 'POST',
      },
    )

    return result?.redirectUrl
  }
}

/** ************************************************************************* */

export default function endpoints(getToken: GetToken, userId?: string | null) {
  const authenticatedApi = apiFactory(getToken, userId)

  const authenticatedCommonEndpoints = commonEndpoints(getToken, userId)

  return {
    analytics: authenticatedCommonEndpoints.analytics,
    editions: {
      ...authenticatedCommonEndpoints.editions,
      get: authenticatedCommonEndpoints.editions.get<Edition>,
      latest: latestEditions(authenticatedApi),
      list: authenticatedCommonEndpoints.editions.list<Edition>,
      search: authenticatedCommonEndpoints.editions.list<Edition>,
    },
    forms: authenticatedCommonEndpoints.forms,
    newsletters: authenticatedCommonEndpoints.newsletters,
    payments: {
      create: generateCheckoutLink(authenticatedApi),
    },
    polls: {
      get: authenticatedCommonEndpoints.polls.get,
      vote: authenticatedCommonEndpoints.polls.vote,
    },
    upload: {
      userPicture: authenticatedCommonEndpoints.upload.userPicture,
    },
    users: {
      ...authenticatedCommonEndpoints.users,
      get: authenticatedCommonEndpoints.users.get<User>(),
      patch: authenticatedCommonEndpoints.users.patch<User>,
    },
  }
}
